import { useForm, FieldError, DeepPartial, FieldValues } from 'react-hook-form';
import { all } from 'deepmerge';
import { useTranslation } from 'react-i18next';
import { ServerError, setServerErrors } from './error-helper';
import { capitalize, attemptErrorTranslation } from './translation-utils';
import { FetchResult } from '@apollo/client';

export interface UseFormHookProps<TData extends FieldValues, TResult> {
  onSubmit: (data: TData) => Promise<FetchResult<TResult>>;
  preventReset?: boolean;
  defaultValues?: DeepPartial<TData>;
  mode?: 'onBlur' | 'onChange' | 'onSubmit' | 'onTouched' | 'all';
  reValidateMode?: 'onBlur' | 'onChange' | 'onSubmit';
  resolver?: any;
}
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
// eslint-disable-next-line max-lines-per-function
export const useFormHook = <TData extends Record<string, unknown>, TResult>({
  defaultValues,
  onSubmit,
  preventReset = false,
  ...rest
}: UseFormHookProps<TData, TResult>) => {
  const useFormResult = useForm<TData>({
    defaultValues: defaultValues as any,
    criteriaMode: 'all',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
    ...rest,
  });

  const { t } = useTranslation();

  // Overwrite "handleSubmit" with custom implementation
  const submitHandler = useFormResult.handleSubmit(async (data) => {
    const result = await onSubmit(data as unknown as TData);
    if (result.data) {
      if (Object.keys(result.data).length === 1) {
        // FIXME: This is kind of a hack.
        const data = result.data as Record<string, unknown>;
        const resultData = data[
          Object.keys(result.data)[0]
        ] as DeepPartial<TData>;
        if (!preventReset) {
          useFormResult.reset(resultData as any, {
            keepIsSubmitSuccessful: true,
          });
        }
      } else if (Object.keys(result.data).length > 1) {
        useFormResult.reset(result.data as any, {
          keepIsSubmitSuccessful: true,
        });
      }
    }
    const apolloError = (result as { error?: unknown }).error;
    if (result.errors?.length) {
      const extensions = all(
        result.errors.map((error) => error.extensions || []),
      ) as unknown as ServerError<TData>;
      setServerErrors(extensions, useFormResult.setError, t);
      return;
    }
    if (apolloError) {
      useFormResult.setError('root', {
        type: 'server',
        message: String(t('global.forms.validationErrors.generic')),
      });
      return;
    }
    return useFormResult.clearErrors();
  });

  // Transform error to show a message by default
  // Attempt to infer translated message from validation key
  // Build a derived errors object without mutating formState
  type ErrorRecord = Record<string, FieldError | undefined>;
  const rawErrors = useFormResult.formState.errors;
  const baseErrors: ErrorRecord = {};
  Object.keys(rawErrors).forEach((field) => {
    baseErrors[field] = rawErrors[field] as FieldError | undefined;
  });
  const enrichedErrors = Object.keys(rawErrors).reduce<ErrorRecord>(
    (acc, field) => {
      const error = acc[field];
      if (!error) return acc;
      const translation = Object.keys(error?.types ?? {})
        .flatMap((type) => {
          return capitalize(attemptErrorTranslation(type, type, t));
        })
        .join('. ');
      if (translation && !error.message) {
        acc[field] = { ...error, message: translation };
      }
      return acc;
    },
    baseErrors,
  );

  return {
    ...useFormResult,
    errors: enrichedErrors,
    handleSubmit: submitHandler,
  };
};
